import React, { useEffect, useRef, useState } from 'react';
import { CustomIcon } from '../UI';
import './ActionsMenu.scss';

const ActionsMenu = ({
  isOpen,
  onClose,
  triggerRef, // Reference to the button that triggers the menu
  title, // Optional title (e.g., "Edit")
  actions = [], // Array of action objects: { label, onClick, icon, variant: 'default' | 'danger' }
  position = 'bottom-right', // 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'
  relativeToItem = false // If true, position relative to parent item instead of viewport
}) => {
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Calculate menu position based on trigger button
  useEffect(() => {
    if (isOpen && triggerRef?.current) {
      if (relativeToItem) {
        // Position relative to parent item (absolute positioning)
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const parentItem = triggerRef.current.closest('.item-list-item');
        
        if (parentItem) {
          const parentRect = parentItem.getBoundingClientRect();
          
          let top = 0;
          let left = 0;

          switch (position) {
            case 'bottom-right':
              top = triggerRect.bottom - parentRect.top + 8;
              left = triggerRect.right - parentRect.left - 180; // Approximate menu width
              break;
            case 'bottom-left':
              top = triggerRect.bottom - parentRect.top + 8;
              left = triggerRect.left - parentRect.left;
              break;
            case 'top-right':
              top = triggerRect.top - parentRect.top - 200; // Approximate menu height
              left = triggerRect.right - parentRect.left - 180;
              break;
            case 'top-left':
              top = triggerRect.top - parentRect.top - 200;
              left = triggerRect.left - parentRect.left;
              break;
            default:
              top = triggerRect.bottom - parentRect.top + 8;
              left = triggerRect.right - parentRect.left - 180;
          }

          setMenuPosition({ top, left });
        }
      } else {
        // Original fixed positioning relative to viewport
        const triggerRect = triggerRef.current.getBoundingClientRect();
        
        let top = 0;
        let left = 0;

        switch (position) {
          case 'bottom-right':
            top = triggerRect.bottom + 8;
            left = triggerRect.right - 180; // Approximate menu width
            break;
          case 'bottom-left':
            top = triggerRect.bottom + 8;
            left = triggerRect.left;
            break;
          case 'top-right':
            top = triggerRect.top - 200; // Approximate menu height
            left = triggerRect.right - 180;
            break;
          case 'top-left':
            top = triggerRect.top - 200;
            left = triggerRect.left;
            break;
          default:
            top = triggerRect.bottom + 8;
            left = triggerRect.right - 180;
        }

        // Ensure menu stays within viewport (will be adjusted after render)
        const padding = 8;
        if (left + 180 > window.innerWidth - padding) {
          left = window.innerWidth - 180 - padding;
        }
        if (left < padding) {
          left = padding;
        }
        if (top < padding) {
          top = triggerRect.bottom + padding;
        }

        setMenuPosition({ top, left });
      }
    }
  }, [isOpen, triggerRef, position, relativeToItem]);

  // Adjust position after menu is rendered to get actual dimensions
  useEffect(() => {
    if (isOpen && menuRef?.current && !relativeToItem) {
      const menuRect = menuRef.current.getBoundingClientRect();
      const padding = 8;
      
      setMenuPosition(prev => {
        let { top, left } = prev;
        
        // Horizontal adjustment
        if (left + menuRect.width > window.innerWidth - padding) {
          left = window.innerWidth - menuRect.width - padding;
        }
        if (left < padding) {
          left = padding;
        }
        
        // Vertical adjustment
        if (top + menuRect.height > window.innerHeight - padding) {
          const triggerRect = triggerRef?.current?.getBoundingClientRect();
          if (triggerRect) {
            top = triggerRect.top - menuRect.height - padding;
          }
        }
        if (top < padding) {
          top = padding;
        }
        
        return { top, left };
      });
    }
  }, [isOpen, triggerRef, relativeToItem]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        triggerRef?.current &&
        !triggerRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      // Use setTimeout to avoid immediate closure on the click that opened the menu
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, triggerRef]);

  if (!isOpen || actions.length === 0) return null;

  const handleActionClick = (action) => {
    if (action.onClick) {
      action.onClick();
    }
    onClose();
  };

  return (
    <>
      {/* Overlay to capture clicks */}
      {isOpen && <div className="actions-menu-overlay" onClick={onClose} />}
      
      {/* Menu */}
      <div
        ref={menuRef}
        className={`actions-menu ${relativeToItem ? 'relative-to-item' : ''}`}
        style={{
          top: `${menuPosition.top}px`,
          left: `${menuPosition.left}px`
        }}
      >
        {title && (
          <>
            <div className="actions-menu__title">{title}</div>
            <div className="actions-menu__separator" />
          </>
        )}
        
        <div className="actions-menu__list">
          {actions.map((action, index) => (
            <button
              key={index}
              className={`actions-menu__item ${
                action.variant === 'danger' ? 'actions-menu__item--danger' : ''
              }`}
              onClick={() => handleActionClick(action)}
            >
              {/* {action.icon && (
                <CustomIcon 
                  name={action.icon} 
                  size={16} 
                  className="actions-menu__item-icon"
                />
              )} */}
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default ActionsMenu;
